import React from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Calendar, Coffee, Info, ExternalLink } from "lucide-react";
import { TOURNAMENT_INFO, ASSETS } from "../data/tournament";
import RevealEmail from "../components/RevealEmail";

const ICES = [
  { name: "Glace Hamelin", note: "Située au CCSSJ", image: ASSETS.bench },
  { name: "Glace Letang", note: "Située au CCSSJ", image: ASSETS.dressing_room },
  { name: "Glace Bâtisseurs", note: "Située au CCSSJ", image: ASSETS.team_ice },
];

const FAQS = [
  {
    q: "Où récupérer les bracelets d'équipe ?",
    a: "Le bureau du tournoi est situé à l'entrée principale du CCSSJ. Ouvert dès 9h00 le vendredi 1er mai.",
  },
  {
    q: "Y a-t-il un service de restauration sur place ?",
    a: "Oui — un casse-croûte chaud est disponible au CCSSJ durant les heures de jeu.",
  },
  {
    q: "Stationnement ?",
    a: "Stationnement gratuit disponible sur place. Arrivez 30 minutes avant votre match.",
  },
  {
    q: "Comment connaître les changements d'horaire ?",
    a: "L'horaire en ligne est mis à jour en temps réel. Les responsables d'équipe seront notifiés directement par courriel.",
  },
];

const Infos = () => {
  const v = TOURNAMENT_INFO.venue;
  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="infos-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Tout ce qu'il faut savoir
          </span>
          <h1 className="mt-3 font-display text-6xl lg:text-8xl uppercase leading-none">
            <span className="text-[#E60012]">Infos</span> pratiques
          </h1>
        </div>
      </section>

      {/* Venue */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-center gap-3 mb-8">
          <MapPin className="w-5 h-5 text-[#E60012]" />
          <h2 className="font-display text-4xl uppercase">L'aréna</h2>
        </div>

        <div className="bg-[#111] border border-white/10 overflow-hidden clip-jersey" data-testid="venue-card">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="aspect-video lg:aspect-auto overflow-hidden">
              <img src={ASSETS.team_ice} alt={v.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center">
              <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012] mb-3">
                Aréna officielle · Toutes les parties
              </div>
              <h3 className="font-display text-4xl lg:text-5xl uppercase leading-tight">{v.name}</h3>
              <p className="mt-2 text-white/60 text-sm">{v.longName}</p>
              <div className="mt-6 space-y-2 text-white/80">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#E60012] mt-0.5 shrink-0" />
                  <div>
                    <div>{v.address}</div>
                    <div>{v.city}</div>
                  </div>
                </div>
              </div>
              <a
                href={v.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="venue-maps-link"
                className="mt-6 inline-flex items-center gap-2 self-start px-5 py-2.5 bg-[#E60012] hover:bg-[#ff1a30] text-white font-condensed uppercase tracking-[0.2em] text-xs transition-all hover:-translate-y-0.5 clip-tab"
              >
                Itinéraire Google Maps <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Glaces */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Info className="w-5 h-5 text-[#E60012]" />
          <h2 className="font-display text-4xl uppercase">Les 3 glaces</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {ICES.map((g, i) => (
            <motion.div
              key={g.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#111] border border-white/10 overflow-hidden clip-jersey hover:border-[#E60012] transition-all"
              data-testid={`ice-${i}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={g.image} alt={g.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#E60012]">
                  Surface de glace
                </div>
                <h3 className="mt-1 font-display text-2xl uppercase">{g.name}</h3>
                <p className="mt-1 text-white/55 text-xs">{g.note}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Quick info cards */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Calendar, title: "Dates", value: TOURNAMENT_INFO.dates },
            { icon: Info, title: "Catégories", value: "M9 · 2017 / 2018" },
            { icon: Coffee, title: "Restauration", value: "Sur place" },
            { icon: Mail, title: "Contact", value: "Voir plus bas" },
          ].map((c, i) => (
            <div
              key={c.title}
              className="bg-[#111] border border-white/10 p-5 hover:border-[#E60012] transition-colors"
              data-testid={`info-card-${i}`}
            >
              <c.icon className="w-5 h-5 text-[#E60012] mb-3" />
              <div className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60">
                {c.title}
              </div>
              <div className="mt-1 font-display text-xl uppercase">{c.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Info className="w-5 h-5 text-[#E60012]" />
          <h2 className="font-display text-4xl uppercase">Foire aux questions</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {FAQS.map((f, i) => (
            <div key={f.q} className="bg-[#111] border-l-2 border-[#E60012] p-6" data-testid={`faq-${i}`}>
              <h3 className="font-condensed uppercase tracking-[0.15em] text-sm text-white">{f.q}</h3>
              <p className="mt-3 text-white/65 text-sm leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact strip */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="bg-gradient-to-r from-[#1a0306] to-[#0a0a0a] border border-[#E60012]/30 p-10 clip-jersey">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
                Une question ?
              </span>
              <h3 className="mt-2 font-display text-4xl lg:text-5xl uppercase leading-tight">
                On est là pour <span className="text-[#E60012]">vous</span>.
              </h3>
            </div>
            <div className="space-y-3 text-white/80">
              <div className="flex items-center gap-3 flex-wrap">
                <Mail className="w-5 h-5 text-[#E60012]" />
                <RevealEmail testid="infos-email" />
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#E60012] mt-0.5" />
                <span>{v.full}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Infos;
